/**
 * @fileoverview Componente molecule NewChatForm para crear un nuevo cliente y chat directo.
 * Permite ingresar datos básicos y campos personalizados, con validación y optimización de renderizado.
 * Optimizado con React.memo, useCallback y useMemo para performance.
 *
 * @module NewChatForm
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización de renderizado.
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import moment from "moment";
import { cloneDeep, forEach } from "lodash";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';

import CustomerModel from '../../../components/Customers/CustomerModel';
import { getCountryOptions, filterOption } from '../../../services/countryService';
import { fetchCustomFields } from '../../../actions/actions';

import InputMC from '../../components/InputMC';
import DropdownMC from '../../components/DropdownMC';
import ToggleSwitchMC from '../../components/ToggleSwitchMC';
import DatePickerMC from '../../components/DatePickerMC';
import FormProviderMC from '../../components/FormProviderMC';

/**
 * @typedef {Object} NewChatFormProps
 * @description Propiedades del componente NewChatForm.
 * @property {function} onSuccess - Callback al crear exitosamente el cliente
 */

/**
 * Componente molecule para crear un nuevo cliente y chat directo.
 *
 * @component NewChatForm
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Formulario con validación para datos básicos y campos personalizados
 * - Permite alternar campos avanzados (email y custom fields)
 * - Optimizado con React.memo, useCallback y useMemo para evitar renders innecesarios
 * - Estilizado con TailwindCSS
 * - Integración con Redux y react-hook-form
 *
 * **Atomic Design:**
 * - Nivel: Molecule
 * - Composición: Usa InputMC, DropdownMC, ToggleSwitchMC, DatePickerMC, FormProviderMC
 * - Dependencias: CustomerModel, getCountryOptions, fetchCustomFields
 *
 * @param {NewChatFormProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento renderizado con el formulario de nuevo chat
 *
 * @example
 * <NewChatForm onSuccess={fn} />
 *
 * @see {@link ../../components/InputMC} Componente de input
 * @see {@link ../../components/DropdownMC} Componente de dropdown
 * @see {@link ../../components/ToggleSwitchMC} Componente de switch
 * @see {@link ../../components/DatePickerMC} Componente de fecha
 * @see {@link ../../components/FormProviderMC} Proveedor de formulario
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */
const NewChatForm = React.memo(({ onSuccess }) => {
  const { t } = useTranslation('NewChat');
  const dispatch = useDispatch();
  const [optionsCountries, setOptionsCountries] = useState([]);
  const [customFieldsGrouped, setCustomFieldsGrouped] = useState([]);
  const [showEmail, setShowEmail] = useState(false);

  /**
   * Muestra un toast de error simple.
   * @function showtoast
   * @memberof NewChatForm
   * @param {string} message - Mensaje a mostrar
   */
  const showtoast = useCallback((message) => {
    alert(message);
  }, []);

  const validationSchema = object().shape({
    first_name: string().required(t('validation.required')),
    last_name: string().required(t('validation.required')),
    phone: string().required(t('validation.required')),
    country_id: string().required(t('validation.required')),
    email: string().email(t('newChatForm.invalidEmail'))
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      country_id: '',
      phone: '',
      email: '',
      send_for_opt_in: true
    }
  });

  const {
    handleSubmit
  } = methods;

  const {
    customFields = []
  } = useSelector((reduxState) => reduxState.mainReducer);

  /**
   * Efecto para cargar países y campos personalizados al montar.
   * @memberof NewChatForm
   */
  useEffect(() => {
    setOptionsCountries(getCountryOptions());
    dispatch(fetchCustomFields());
  }, [dispatch]);

  /**
   * Actualiza el valor de un campo personalizado en el estado.
   * @function setValues
   * @memberof NewChatForm
   * @param {string} fieldName - Identificador del campo
   * @param {any} value - Valor a asignar
   */
  const setValues = useCallback((fieldName, value) => {
    const clonedCustomFieldsGrouped = cloneDeep(customFieldsGrouped);
    const fieldIn = clonedCustomFieldsGrouped.find((el) => el.identifier === fieldName);
    if (fieldIn) {
      const updatedField = { ...fieldIn, answer: value };
      const updatedFields = clonedCustomFieldsGrouped.map((field) => (
        field.identifier === fieldName ? updatedField : field
      ));
      setCustomFieldsGrouped(updatedFields);
    }
  }, [customFieldsGrouped]);

  /**
   * Efecto para agrupar y preparar campos personalizados.
   * @memberof NewChatForm
   */
  useEffect(() => {
    const clonedCustomFileds = cloneDeep(customFields);
    forEach(clonedCustomFileds, (field) => {
      const updatedField = { ...field };
      updatedField.answer = field.field_type === 'boolean' ? false : '';
      updatedField.id_saved = null;
      if (field.field_type === 'list') {
        updatedField.list_options = field.list_options.map((option) => ({ value: option.key, label: option.value }));
      }
      return updatedField;
    });
    setCustomFieldsGrouped(clonedCustomFileds);
  }, [customFields]);

  /**
   * Maneja el cambio de valor en campos personalizados.
   * @function handleInputChangeCustomField
   * @memberof NewChatForm
   * @param {Object} e - Evento de cambio
   * @param {Object} field - Campo personalizado
   */
  const handleInputChangeCustomField = useCallback((e, field) => {
    const { target } = e;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    if (field.field_type === 'integer') {
      value = value.replace(/\D/g, '');
    }
    if (field.field_type === 'float') {
      const regex = /([0-9]*[.,]{0,1}[0-9]{0,2})/s;
      const [match] = value.match(regex) || [''];
      value = match;
    }
    setValues(name, value);
  }, [setValues]);

  /**
   * Maneja la selección de opción en campos tipo lista.
   * @function handleCustomFieldSelect
   * @memberof NewChatForm
   * @param {Object} option - Opción seleccionada
   * @param {Object} field - Campo personalizado
   */
  const handleCustomFieldSelect = useCallback((option, field) => {
    setValues(field.identifier, option.value);
  }, [setValues]);

  /**
   * Renderiza el campo personalizado según su tipo.
   * @function printCustomField
   * @memberof NewChatForm
   * @param {Object} field - Campo personalizado
   * @returns {JSX.Element|null} Elemento renderizado del campo
   */
  const printCustomField = useCallback((field) => {
    switch (field.field_type) {
      case 'string':
      case 'integer':
      case 'float':
        return (
          <InputMC
            id={`custom-field-${field.identifier}`}
            name={field.identifier}
            label={field.name}
            placeholder={field.name}
            value={field.answer}
            onChange={(fieldKey, value) => {
              // Crear el evento sintético para mantener compatibilidad
              const syntheticEvent = {
                target: {
                  value,
                  name: field.identifier
                }
              };
              handleInputChangeCustomField(syntheticEvent, field);
            }}
            useFloatingLabel
          />
        );
      case 'date':
        return (
          <DatePickerMC
            label={field.name}
            name={field.identifier}
            dateFormat="yyyy-MM-dd"
            showYearDropdown
          />
        );
      case 'boolean':
        return (
          <div className="tw-flex tw-items-center tw-space-x-3">
            <ToggleSwitchMC
              isOn={field.answer}
              handleToggle={(checked) => handleInputChangeCustomField(
                { target: { checked, name: field.identifier, type: 'checkbox' } },
                field
              )}
              size="small"
            />
            <span className="tw-text-sm tw-text-gray-700">{field.name}</span>
          </div>
        );
      case 'list':
        return (
          <DropdownMC
            id={`custom-field-${field.identifier}`}
            name={field.identifier}
            label={field.name}
            placeholder={t('newChatForm.selectOption')}
            options={field.list_options.map((option) => ({
              id: option.value,
              label: option.label
            }))}
            defaultValue={field.answer}
            onSelect={(value) => handleCustomFieldSelect({ value }, field)}
            useFloatingLabel
          />
        );
      default:
        return null;
    }
  }, [handleInputChangeCustomField, handleCustomFieldSelect, t]);

  /**
   * Maneja el submit del formulario y crea el cliente.
   * @function onSubmit
   * @memberof NewChatForm
   * @param {Object} data - Datos del formulario
   */
  const onSubmit = useCallback(async (data) => {
    const customerData = { ...data };
    if (!showEmail) { delete customerData.email; }
    customerData.customer_related_data_attributes = [];
    forEach(customFieldsGrouped, (field) => {
      const customer_related_data = {
        customer_related_field_id: field.id
      };
      switch (field.field_type) {
        case 'date':
          customer_related_data.data = field.answer ? moment(field.answer).format("YYYY-MM-DD") : '';
          break;
        default:
          customer_related_data.data = field.answer;
      }
      if (field.id_saved) customer_related_data.id = field.id_saved;
      customerData.customer_related_data_attributes.push(customer_related_data);
    });
    const formData = { customer: customerData };
    try {
      const response = await CustomerModel.createCustomerFromDirectMessage(formData);
      dispatch({ type: 'CREATE_CUSTOMER_FROM_CHAT', data: response });
      onSuccess();
    } catch (error) {
      showtoast(error.message);
    }
  }, [showEmail, customFieldsGrouped, dispatch, onSuccess, showtoast]);

  /**
   * Memoiza el renderizado de los campos personalizados para performance.
   * @function renderedCustomFields
   * @memberof NewChatForm
   * @returns {JSX.Element[]} Elementos renderizados de campos personalizados
   */
  const renderedCustomFields = useMemo(() => (
    customFieldsGrouped && customFieldsGrouped.map((field) => (
      <div key={`customer-custom-field-${field.id}-${field.identifier}`}>
        {printCustomField(field)}
      </div>
    ))
  ), [customFieldsGrouped, printCustomField]);

  /**
   * Función de filtro personalizada para el dropdown de países.
   * @function countryFilterOption
   * @memberof NewChatForm
   * @param {string} searchTerm - Término de búsqueda
   * @returns {Array} Opciones filtradas
   */
  const countryFilterOption = useCallback((searchTerm) => {
    if (!searchTerm) return optionsCountries;
    return optionsCountries.filter((country) => {
      // Validar que el objeto country existe y tiene las propiedades necesarias
      if (!country || !country.name_es || !country.name_en || !country.dial_code) {
        return false;
      }
      // Transformar el objeto al formato esperado por filterOption
      const formattedOption = {
        value: country.value,
        data: country
      };
      return filterOption(formattedOption, searchTerm);
    });
  }, [optionsCountries]);

  /**
   * Renderiza el formulario de nuevo chat y cliente.
   * @memberof NewChatForm
   * @returns {JSX.Element}
   */
  return (
    <FormProviderMC
      id="new-chat-form"
      methods={methods}
      onSubmit={handleSubmit(onSubmit)}
      className="tw-flex tw-flex-col tw-h-full"
    >
      {/* Form Content */}
      <div className="tw-flex-1 tw-space-y-6">
        <InputMC
          id="first_name"
          name="first_name"
          label={t('newChatForm.firstNameLabel')}
          placeholder={t('newChatForm.firstNameLabel')}
          useFloatingLabel
        />

        <InputMC
          id="last_name"
          name="last_name"
          label={t('newChatForm.lastNameLabel')}
          placeholder={t('newChatForm.lastNameLabel')}
          useFloatingLabel
        />

        <DropdownMC
          search
          id="country_id"
          name="country_id"
          label={t('newChatForm.countryLabel')}
          options={optionsCountries}
          renderOption={(option) => option.label}
          renderSelected={(option) => (option ? option.label : t('newChatForm.selectCountry'))}
          filterOption={countryFilterOption}
          useFloatingLabel
        />

        <InputMC
          id="phone"
          name="phone"
          label={t('newChatForm.phoneLabel')}
          placeholder={t('newChatForm.phoneLabel')}
          useFloatingLabel
        />

        {/* Advanced Options Toggle */}
        <div className="tw-flex tw-items-center tw-space-x-3">
          <ToggleSwitchMC
            isOn={showEmail}
            handleToggle={() => setShowEmail(!showEmail)}
            size="small"
          />
          <span className="tw-text-sm tw-text-gray-700">{t('newChatForm.advancedOptions')}</span>
        </div>

        {showEmail && (
          <div className="tw-space-y-6">
            <InputMC
              id="email"
              name="email"
              label={t('newChatForm.emailLabel')}
              placeholder={t('newChatForm.emailLabel')}
              type="email"
              useFloatingLabel
            />

            {renderedCustomFields}
          </div>
        )}
      </div>

    </FormProviderMC>
  );
});

/**
 * Exportación principal del componente NewChatForm.
 * @type {React.MemoExoticComponent}
 */
export default NewChatForm;
